import { type ChangeEvent, useCallback, useEffect, useState } from "react"
import { Button, FormControl, Modal, Table } from "react-bootstrap"
import Loader from "src/components/common/Loader"
import snackBar from "src/utilities/snackBar/SnackBar"
import getPositiveResponseCodes from "./crud/GetPositiveResponseCodes"
import updatePositiveResponseCode from "./crud/UpdatePositiveResponseCode"
import type {
	PositiveResponseCode,
	PositiveResponseCodeEditableFields,
} from "./interface"
import "./PositiveResponseCodes.css"
import Checkbox from "src/components/common/checkbox"

const MAX_CODE_LENGTH = 50
const MAX_SHORT_DESCRIPTION_LENGTH = 100
const MAX_LONG_DESCRIPTION_LENGTH = 200
const MAX_HELP_TEXT_LENGTH = 2000

const EMPTY_FORM: PositiveResponseCodeEditableFields = {
	Code: "",
	ShortDescription: "",
	LongDescription: "",
	HelpText: "",
	SendToClient: false,
	IsCommentRequired: false,
	IsAllowedOnCompletedTicket: false
}

function PositiveResponseCodes() {
	const [loading, setLoading] = useState(true)
	const [codes, setCodes] = useState<PositiveResponseCode[]>([])
	const [filteredCodes, setFilteredCodes] = useState<PositiveResponseCode[]>([])
	const [selectedCode, setSelectedCode] = useState<PositiveResponseCode | null>(
		null,
	)
	const [saving, setSaving] = useState(false)
	const [searchString, setSearchString] = useState("")
	const [form, setForm] = useState<PositiveResponseCodeEditableFields>(EMPTY_FORM)

	const fetchCodes = useCallback(async () => {
		setLoading(true)
		try {
			const response = await getPositiveResponseCodes()

			if (!response.success) throw new Error()

			setCodes(response.response.Data)
			setFilteredCodes(response.response.Data)
		} catch (_error) {
			snackBar({
				type: "error",
				text: "Error fetching positive response codes",
				timeout: 4000,
			})
		} finally {
			setLoading(false)
		}
	}, [])

	const handleSearchDelay = useCallback(() => {
		if (searchString === "") {
			setFilteredCodes(codes)
			return
		}
		const delaySearch = setTimeout(() => {
			const filtered = codes.filter(
				(x) =>
					x.Code?.toLowerCase().includes(searchString.toLowerCase()) ||
					x.HelpText?.toLowerCase().includes(searchString.toLowerCase()) ||
					x.ShortDescription?.toLowerCase().includes(
						searchString.toLowerCase(),
					) || 
					x.LongDescription?.toLowerCase().includes(
						searchString.toLowerCase(),
					),
			)
			setFilteredCodes(filtered)
		}, 300)
		return () => clearTimeout(delaySearch)
	}, [searchString, codes])

	const handleEditClick = (code: PositiveResponseCode) => {
		setSelectedCode(code)
		setForm({
			Code: code.Code ?? "",
			ShortDescription: code.ShortDescription ?? "",
			LongDescription: code.LongDescription ?? "",
			HelpText: code.HelpText ?? "",
			SendToClient: code.SendToClient,
			IsCommentRequired: code.IsCommentRequired,
			IsAllowedOnCompletedTicket: code.IsAllowedOnCompletedTicket
		})
	}

	const handleSave = async () => {
		if (!selectedCode?.PositiveResponseCodeID) return

		setSaving(true)
		try {
			const response = await updatePositiveResponseCode(
				selectedCode.PositiveResponseCodeID,
				form,
			)
			if (!response.success) {
				snackBar({
					type: "error",
					text: response.errorMessage ?? "Error when updating positive response code",
					timeout: 4000,
				})
			} else {
				await fetchCodes()
				handleCancel()
				snackBar({
					type: "success",
					text: "Successfully updated positive response code",
					timeout: 4000,
				})
			}
		} catch (_error) {
			snackBar({
				type: "error",
				text: "Error when updating positive response code",
				timeout: 4000,
			})
		} finally {
			setSaving(false)
		}
	}

	const handleCancel = () => {
		setSelectedCode(null)
		setForm(EMPTY_FORM)
	}

	const handleFieldChange = <K extends keyof PositiveResponseCodeEditableFields>(
		field: K,
		value: PositiveResponseCodeEditableFields[K],
	) => {
		setForm((prev) => ({ ...prev, [field]: value }))
	}

	useEffect(() => {
		fetchCodes()
	}, [fetchCodes])

	useEffect(() => {
		return handleSearchDelay()
	}, [handleSearchDelay])

	if (loading)
		return (
			<Loader
				title="Loading Positive Response Codes"
				styles={{ margin: "40px auto", display: "block", textAlign: "center" }}
			/>
		)

	return (
		<div className="positive-response-container">
			<h4 className="positive-response-title">Positive Response Codes</h4>
			<div className="positive-response-search-container">
				<div className="positive-response-search-wrapper">
					<FormControl
						disabled={codes.length === 0}
						placeholder="Search"
						onChange={(event: ChangeEvent<HTMLInputElement>) =>
							setSearchString(event.target.value)
						}
						value={searchString || ""}
					/>
					<Button
						onClick={() => {
							if (searchString && searchString.length > 0) setSearchString("")
						}}
						variant="secondary"
					>
						<span className="material-icons-outlined">
							{searchString && searchString.length > 0 ? "close" : "search"}
						</span>
					</Button>
				</div>
			</div>
			<div className="positive-response-table-wrapper">
				<Table striped bordered hover>
					<colgroup>
						<col className="positive-response-col-code" />
						<col className="positive-response-col-short-desc" />
						<col />
						<col />
						<col className="positive-response-col-flag" />
						<col className="positive-response-col-flag" />
						<col className="positive-response-col-flag" />
						<col className="positive-response-col-edit" />
					</colgroup>
					<thead className="positive-response-table-head">
						<tr>
							<th>Code</th>
							<th>Short Description</th>
							<th>Long Description</th>
							<th>Work Item Description</th>
							<th>Send to Client</th>
							<th>Comment Required</th>
							<th>Allowed On Completed Ticket</th>
							<th>Edit</th>
						</tr>
					</thead>
					<tbody>
						{(() => {
							if (filteredCodes.length === 0)
								return (
									<tr>
										<td colSpan={8} className="positive-response-empty-row">
											No results found
										</td>
									</tr>
								)
							return filteredCodes.map((code) => (
								<tr key={code.PositiveResponseCodeID}>
									<td>{code.Code}</td>
									<td>{code.ShortDescription}</td>
									<td>{code.LongDescription}</td>
									<td>{code.HelpText}</td>
									<td>
										<Checkbox isChecked={code.SendToClient} onChange={null} isDisabled={true} />
									</td>
									<td>
										<Checkbox isChecked={code.IsCommentRequired} onChange={null} isDisabled={true} />
									</td>
									<td>
										<Checkbox isChecked={code.IsAllowedOnCompletedTicket} onChange={null} isDisabled={true} />
									</td>
									<td>
										<Button
											size="sm"
											variant="outline-primary"
											onClick={() => handleEditClick(code)}
										>
											Edit
										</Button>
									</td>
								</tr>
							))
						})()}
					</tbody>
				</Table>
			</div>

			{selectedCode && (
				<Modal
					show={true}
					onHide={handleCancel}
					centered
					backdrop="static"
					size="lg"
				>
					<Modal.Header closeButton>
						<Modal.Title>Edit Positive Response Code</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<label
							htmlFor="code-input"
							className="positive-response-field-label"
						>
							Code
						</label>
						<FormControl
							id="code-input"
							className="positive-response-code-input"
							value={form.Code}
							maxLength={MAX_CODE_LENGTH}
							onChange={(event: ChangeEvent<HTMLInputElement>) =>
								handleFieldChange("Code", event.target.value)
							}
						/>
						<div className="positive-response-field-counter">
							{form.Code.length} / {MAX_CODE_LENGTH}
						</div>

						<label
							htmlFor="short-description-input"
							className="positive-response-field-label"
						>
							Short Description
						</label>
						<FormControl
							id="short-description-input"
							maxLength={MAX_SHORT_DESCRIPTION_LENGTH}
							value={form.ShortDescription}
							onChange={(event: ChangeEvent<HTMLInputElement>) =>
								handleFieldChange("ShortDescription", event.target.value)
							}
						/>
						<div className="positive-response-field-counter">
							{form.ShortDescription.length} / {MAX_SHORT_DESCRIPTION_LENGTH}
						</div>

						<label
							htmlFor="long-description-input"
							className="positive-response-field-label"
						>
							Long Description
						</label>
						<textarea
							id="long-description-input"
							className="positive-response-textarea positive-response-textarea-short"
							maxLength={MAX_LONG_DESCRIPTION_LENGTH}
							value={form.LongDescription}
							onChange={(event) =>
								handleFieldChange("LongDescription", event.currentTarget.value)
							}
						/>
						<div className="positive-response-field-counter">
							{form.LongDescription.length} / {MAX_LONG_DESCRIPTION_LENGTH}
						</div>

						<label
							htmlFor="help-text-input"
							className="positive-response-field-label"
						>
							Work Item Description (shown to technician)
						</label>
						<textarea
							id="help-text-input"
							className="positive-response-textarea positive-response-textarea-tall"
							maxLength={MAX_HELP_TEXT_LENGTH}
							value={form.HelpText}
							onChange={(event) =>
								handleFieldChange("HelpText", event.currentTarget.value)
							}
						/>
						<div className="positive-response-field-counter">
							{form.HelpText.length} / {MAX_HELP_TEXT_LENGTH}
						</div>

						<div className="positive-response-checkbox-group">
							<Checkbox
								id="send-to-client-checkbox"
								label="Send to Client"
								isChecked={form.SendToClient}
								onChange={(checked: boolean) =>
									handleFieldChange("SendToClient", checked)
								}
							/>
							<Checkbox
								id="comment-required-checkbox"
								label="Comment Required"
								isChecked={form.IsCommentRequired}
								onChange={(checked: boolean) =>
									handleFieldChange("IsCommentRequired", checked)
								}
							/>
							<Checkbox
								id="allowed-on-completed-checkbox"
								label="Allowed On Completed Ticket"
								isChecked={form.IsAllowedOnCompletedTicket}
								onChange={(checked: boolean) =>
									handleFieldChange("IsAllowedOnCompletedTicket", checked)
								}
							/>
						</div>
					</Modal.Body>
					<Modal.Footer>
						<Button variant="secondary" onClick={handleCancel}>
							Cancel
						</Button>
						<Button variant="primary" onClick={handleSave} disabled={saving}>
							{saving ? "Saving..." : "Save"}
						</Button>
					</Modal.Footer>
				</Modal>
			)}
		</div>
	)
}

export default PositiveResponseCodes
